import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Wand2, Check, SkipForward } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const Dubbio = ({ d, onScelta, onSalta }) => (
  <div className="border rounded-xl p-3" data-testid={`foto-dubbio-${d.vino_id}`}>
    <div className="flex items-start justify-between gap-2">
      <p className="text-sm font-semibold text-gray-900 leading-tight">{d.nome}<span className="block text-xs font-normal text-gray-500">{d.cantina}</span></p>
      <button onClick={() => onSalta(d)} className="text-xs text-gray-500 hover:text-gray-800 flex items-center gap-1 shrink-0" data-testid={`foto-salta-${d.vino_id}`}>
        <SkipForward className="h-3.5 w-3.5" /> Nessuna
      </button>
    </div>
    <div className="grid grid-cols-4 gap-2 mt-2">
      {d.candidati.map((c, i) => (
        <button key={c.url} onClick={() => onScelta(d, c)} title={c.titolo} data-testid={`foto-candidato-${d.vino_id}-${i}`}
          className="group border rounded-lg bg-gray-50 h-28 p-1 flex flex-col items-center justify-between hover:border-purple-500 transition-colors">
          <img src={c.thumbnail} alt={c.titolo} loading="lazy" className="max-h-20 object-contain" />
          <span className="text-[9px] leading-tight text-gray-600 line-clamp-2 text-center group-hover:text-purple-700">{c.titolo}</span>
          {c.stessa_cantina === false && <span className="text-[8px] uppercase tracking-wide text-amber-700" data-testid={`foto-altra-cantina-${d.vino_id}-${i}`}>altra cantina</span>}
        </button>
      ))}
    </div>
  </div>
);

export const FotoBottiglieSection = ({ getAuthHeaders }) => {
  const [job, setJob] = useState(null);
  const [avvio, setAvvio] = useState(false);
  const timer = useRef(null);

  const leggi = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/carta-vini/immagini/stato`, { headers: getAuthHeaders() });
      setJob(r.data);
      return r.data;
    } catch (e) { return null; }
  }, [getAuthHeaders]);

  const segui = useCallback(() => {
    clearInterval(timer.current);
    timer.current = setInterval(async () => {
      const j = await leggi();
      if (!j || j.stato !== 'in_corso') clearInterval(timer.current);
    }, 2000);
  }, [leggi]);

  useEffect(() => {
    leggi().then(j => { if (j?.stato === 'in_corso') segui(); });
    return () => clearInterval(timer.current);
  }, [leggi, segui]);

  const avvia = async () => {
    setAvvio(true);
    try {
      const r = await axios.post(`${API}/api/carta-vini/immagini/avvia`, { solo_mancanti: true }, { headers: getAuthHeaders() });
      if (!r.data.totale) toast.info('Tutti i vini hanno già una foto');
      await leggi();
      segui();
    } catch (e) {
      toast.error('Ricerca foto non avviata');
    }
    setAvvio(false);
  };

  const togli = async (d, scelta = false) => {
    try {
      await axios.post(`${API}/api/carta-vini/immagini/scarta-dubbio`, { vino_id: d.vino_id, scelta }, { headers: getAuthHeaders() });
      setJob(j => ({ ...j, assegnati: (j.assegnati || 0) + (scelta ? 1 : 0), dubbi: (j.dubbi || []).filter(x => x.vino_id !== d.vino_id) }));
    } catch (e) {
      toast.error('Operazione non riuscita, riprova');
    }
  };

  const scegli = async (d, c) => {
    try {
      await axios.post(`${API}/api/magazzino/vini/aggiorna`, { vino_id: d.vino_id, immagine_url: c.url }, { headers: getAuthHeaders() });
    } catch (e) {
      toast.error('Errore salvataggio foto');
      return;
    }
    await togli(d, true);
    toast.success(`Foto abbinata: ${d.nome}`);
  };

  const inCorso = job?.stato === 'in_corso';
  const dubbi = job?.dubbi || [];
  const perc = job?.totale ? Math.round((job.fatti / job.totale) * 100) : 0;

  return (
    <div className="border rounded-xl p-3 space-y-3" data-testid="foto-bottiglie-section">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold">Foto delle bottiglie</p>
          <p className="text-[11px] text-gray-500">Cerca su Vivino e abbina da sola le foto sicure; le dubbie le scegli tu.</p>
        </div>
        <button onClick={avvia} disabled={avvio || inCorso} data-testid="foto-trova-btn"
          className="shrink-0 px-3 py-2 bg-purple-600 text-white rounded-lg text-xs font-medium flex items-center gap-1.5 disabled:opacity-50">
          {avvio || inCorso ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Trova foto
        </button>
      </div>
      {job && job.stato !== 'mai_avviato' && (
        <div className="text-xs text-gray-600 space-y-1.5" data-testid="foto-stato">
          {inCorso && (
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-purple-600 transition-[width] duration-500" style={{ width: `${perc}%` }} /></div>
          )}
          <p>
            {inCorso ? `Ricerca in corso… ${job.fatti}/${job.totale}` : job.stato === 'interrotto' ? 'Ricerca interrotta: avviala di nuovo' : `Ricerca completata: ${job.totale} vini analizzati`}
            {' · '}<b className="text-green-700" data-testid="foto-assegnate"><Check className="inline h-3 w-3" /> {job.assegnati || 0} abbinate</b>
            {' · '}<b className="text-amber-700" data-testid="foto-da-scegliere">{dubbi.length} da scegliere</b>
            {' · '}{job.non_trovati || 0} non trovate
          </p>
        </div>
      )}
      {!inCorso && dubbi.length > 0 && (
        <div className="space-y-2 max-h-80 overflow-y-auto pr-1" data-testid="foto-dubbi-lista">
          <p className="text-xs text-gray-600">Tocca la foto giusta (oppure "Nessuna"):</p>
          {dubbi.map(d => <Dubbio key={d.vino_id} d={d} onScelta={scegli} onSalta={togli} />)}
        </div>
      )}
    </div>
  );
};
